import { computed, effect, Injectable, signal } from '@angular/core';
import { JSONTypes, SendMessageError, WebPubSubClient, WebPubSubClientCredential } from '@azure/web-pubsub-client';
import { firstValueFrom, Subject } from 'rxjs';
import { SearchService } from './search.service';
import { SpaceService } from './space.service';
import { UserService } from './user.service';
export interface WebSocketMessage {
  compression: string;
  message: string | object;
  type: string;
  payload: any;
  chunks: number;
  id?: string;
  chunk_index?: number;
}
@Injectable({ providedIn: 'root' })
export class WebsocketService {
  public webSocketStatus = signal<'connected' | 'disconnected' | 'error' | 'stopped'>('disconnected');
  private ws: WebPubSubClient | null = null;
  private isStopped: boolean = false;
  private isConnecting = false;
  private reconnectAttempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly RECONNECT_BASE_DELAY_MS = 1000;
  private readonly RECONNECT_MAX_DELAY_MS = 30000;
  private messageBuffers: { [id: string]: { buffer: string[]; chunks: number } } = {};
  private receivedChunks: { [id: string]: Set<number> } = {};
  private webSocketSpaceId: string = '';
  public decompressionService: any;
  private wsMessageSubject = new Subject<WebSocketMessage>();
  public message$ = this.wsMessageSubject.asObservable();
  private joinedGroups = new Set<string>();
  private currentHubId = computed(() => this.userService.getUserData()?.company?.uuid || '');

  constructor(
    private searchService: SearchService,
    private userService: UserService,
    private spaceService: SpaceService,
  ) {
    effect((onCleanup) => {
      const hubId = this.currentHubId();

      if (hubId) {
        this.searchService.setSearchId();
        this.initializeWebSocket();
      }

      onCleanup(() => {
        this.closeWebSocket();
      });
    });

    /**
     * This effect handles groups that should be joined as soon as 'connected'
     */
    effect(() => {
      const status = this.webSocketStatus();
      const hubId = this.currentHubId();

      if (status === 'connected' && hubId) {
        this.joinSearchWebSocketGroup();
        this.joinWebSocketSpaceGroup();
      }
    });

    effect(() => {
      // this.closeWebSocket();
      const currentActiveSpaceId = this.spaceService.currentActiveSpaceId();
      const status = this.webSocketStatus();

      if (!currentActiveSpaceId || status !== 'connected') return;

      if (this.webSocketSpaceId && this.webSocketSpaceId !== currentActiveSpaceId) {
        this.leaveWebSocketGroup(this.webSocketSpaceId);
      }

      this.webSocketSpaceId = currentActiveSpaceId;
      this.joinWebSocketSpaceGroup();
    });
  }

  /** Initializes the WebSocket connection */
  public initializeWebSocket(): void {
    const hubId = this.currentHubId();
    if (!hubId || this.isConnecting) return;

    this.teardownCurrentClient();

    const credential: WebPubSubClientCredential = {
      getClientAccessUrl: () => firstValueFrom(this.spaceService.getWebsocketToken(hubId)).then((token) => token.url),
    };

    this.isStopped = false;
    this.isConnecting = true;
    void this.connectWebSocket(credential);
  }

  private reconnectWebSocket(): void {
    if (this.isStopped) return;

    if (this.reconnectTimer) {
      this.logInfo('Reconnect already scheduled.');
      return;
    }

    const delay = Math.min(this.RECONNECT_BASE_DELAY_MS * 2 ** this.reconnectAttempt, this.RECONNECT_MAX_DELAY_MS);
    this.reconnectAttempt += 1;

    this.logWarning(`Reconnecting WebSocket in ${delay}ms...`);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.initializeWebSocket();
    }, delay);
  }

  /**
   * Tears down any existing connection and starts a fresh one, regardless of
   * the current `webSocketStatus`. Use when the status signal cannot be trusted
   * (e.g. zombie connections after laptop sleep or long tab inactivity).
   */
  public forceReconnect(): void {
    this.logInfo('Forcing WebSocket reconnect.');
    this.closeWebSocket();
    this.initializeWebSocket();
  }

  public closeWebSocket(): void {
    this.isStopped = true;
    this.isConnecting = false;
    this.clearReconnectTimer();
    this.reconnectAttempt = 0;
    this.teardownCurrentClient();
    this.webSocketStatus.set('stopped');
  }

  private clearReconnectTimer(): void {
    if (!this.reconnectTimer) return;

    clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
  }

  private teardownCurrentClient(): void {
    if (this.ws) {
      this.ws.stop();
      this.ws = null;
    }

    this.joinedGroups.clear();
    this.webSocketSpaceId = '';
  }

  /** Handles WebSocket connection */
  private async connectWebSocket(credential: WebPubSubClientCredential): Promise<void> {
    const ws = new WebPubSubClient(credential, { autoReconnect: false });
    this.ws = ws;
    // this.ws = new WebPubSubClient(credential, { autoReconnect: true, reconnectRetryOptions: { maxRetries: 3, mode: "Exponential", retryDelayInMs: 1000 }});

    ws.on('server-message', (e) => {
      if (this.ws !== ws) return;
      this.handleMessage(this.parseJson(e.message.data) as WebSocketMessage);
    });

    ws.on('group-message', (e) => {
      if (this.ws !== ws) return;
      this.handleGroupMessage(e);
    });

    ws.on('connected', () => {
      if (this.ws !== ws) return;

      this.logInfo('WebSocket connected.');
      this.webSocketStatus.set('connected');
      this.reconnectAttempt = 0;
      this.clearReconnectTimer();
      this.joinSearchWebSocketGroup();
      this.joinWebSocketSpaceGroup();
    });

    ws.on('disconnected', () => {
      if (this.ws !== ws) return;

      this.webSocketStatus.set('disconnected');
      this.joinedGroups.clear();
      this.webSocketSpaceId = '';

      if (!this.isStopped) {
        this.logInfo('WebSocket disconnected - explicitly reconnecting.');
        this.reconnectWebSocket();
      }
    });

    ws.on('stopped', () => {
      if (this.ws !== ws) return;

      if (this.isStopped) {
        this.logInfo('WebSocket explicitly stopped.');
        this.isStopped = false;
      } else {
        this.logInfo('WebSocket stopped unexpectedly.');
        this.reconnectWebSocket();
      }
      this.webSocketStatus.set('stopped');
    });

    try {
      await ws.start();
    } catch {
      if (this.ws === ws && !this.isStopped) {
        this.webSocketStatus.set('error');
        this.reconnectWebSocket();
      }
    } finally {
      if (this.ws === ws) {
        this.isConnecting = false;
      }
    }
  }

  /** Joins the WebSocket search messages group */
  private joinSearchWebSocketGroup(): void {
    if (!this.ws || !this.searchService.searchId() || this.webSocketStatus() !== 'connected') return;

    try {
      this.ws.joinGroup(this.searchService.searchId());
      this.joinedGroups.add(this.searchService.searchId());
    } catch (e) {
      if (e instanceof SendMessageError) {
        this.logError(`Error joining group: ${e.message}`);
      }
    }
  }

  /** Join the WebSocket space messages group */
  private joinWebSocketSpaceGroup(): void {
    const groupId = this.spaceService.currentActiveSpaceId();

    if (!this.ws || !groupId || this.webSocketStatus() !== 'connected') return;
    // Check if already joined
    if (this.joinedGroups.has(groupId)) {
      return;
    }

    try {
      this.ws.joinGroup(groupId);
      this.joinedGroups.add(groupId);
    } catch (e) {
      if (e instanceof SendMessageError) {
        this.logError(`Error joining group: ${e.message}`);
      }
    }
  }

  private leaveWebSocketGroup(groupId: string): void {
    if (!this.ws || !groupId) return;

    if (!this.joinedGroups.has(groupId)) {
      this.logInfo(`Group ${groupId} not currently tracked as joined. Skipping leave.`);
      return;
    }

    try {
      this.ws.leaveGroup(groupId);
      this.joinedGroups.delete(groupId);
    } catch (e) {
      if (e instanceof SendMessageError) {
        this.logError(`Error leaving group: ${e.message}`);
      }
    }
  }

  /** Handles group messages */
  private handleGroupMessage(event: any): void {
    if (
      event.message.group === this.searchService.searchId() ||
      event.message.group === this.spaceService.currentActiveSpaceId()
    ) {
      const message: any = this.parseJson(event.message.data);

      if (message.chunks === 1) {
        this.handleMessage(message);
      } else {
        this.bufferMessageChunks(message);
      }
    }
  }

  private handleMessage(message: WebSocketMessage): void {
    this.wsMessageSubject.next(message);
  }

  /** Buffers multi-chunk messages */
  private bufferMessageChunks(message: any): void {
    const { id, chunk_index, chunks } = message;

    // Initialize storage for this id if not already present
    if (!this.messageBuffers[id]) {
      this.messageBuffers[id] = { buffer: [], chunks };
      this.receivedChunks[id] = new Set();
    }

    this.messageBuffers[id].buffer[chunk_index] = message.message;
    this.receivedChunks[id].add(chunk_index);

    // Check if we have all expected chunks
    if (this.receivedChunks[id].size === chunks) {
      const orderedMessage = this.messageBuffers[id].buffer.join('');

      // Call handleMessage with the reconstructed message
      this.handleMessage({ ...message, message: orderedMessage });

      // Clean up stored data for this id
      delete this.messageBuffers[id];
      delete this.receivedChunks[id];
    }
  }

  private parseJson(data: JSONTypes | ArrayBuffer): JSONTypes {
    return data instanceof ArrayBuffer ? JSON.parse(new TextDecoder('utf-8').decode(data)) : data;
  }

  private logInfo(message: string): void {
    console.info(`[INFO] ${message}`);
  }

  private logWarning(message: string): void {
    console.warn(`[WARNING] ${message}`);
  }

  private logError(message: string): void {
    console.error(`[ERROR] ${message}`);
  }
}
